/**
 * EHV-770: Helper on Mietervertrag / SEV-Vertrag → Mietzahlung after full-month rent row exists.
 * Inserts a compact panel above the rent plan table with a calculator for the first
 * partial month (Kalendertage, 30/360, or fixed %).
 *
 * MV tenant contracts use TenantContractEditor. SEV rent plans live on the same
 * Mietzahlung UI and also on OwnerContractEditor (SEV Eigentümervertrag).
 */

const LUX_PRORATED_HOST_CLASS = 'lux-prorated-rent-host'
const LUX_CONTRACT_EDITOR_SELECTOR =
  '.TenantContractEditor, .OwnerContractEditor'
const LUX_OVERLAY_HOST_SELECTOR = '.overlay-page, .overlay-wrapper'

// Only remember a return from a rent-plan child route, never a general preferred step.
// Keeping this in memory leaves direct navigation to Vertragsdetails unchanged.
let luxRentLastPath = null
let luxRentReturnPath = null
let luxRentRestoreUntil = 0
let luxRentRestoreAttempt = 0
let luxProratedCloseModal = null
let luxRentRestoring = false

function onLuxRentSectionClick(event) {
  if (luxRentRestoring || !event.target.closest) return
  if (
    event.target.closest(
      '.TenantContractEditor .section-header, .OwnerContractEditor .section-header'
    )
  ) {
    luxRentRestoreUntil = 0
    luxRentReturnPath = null
  }
}

function luxRentRoute(pathname = window.location.pathname) {
  const match = pathname.match(/^(.*\/contract\/all\/edit\/[^/]+)(\/.*)?$/)
  if (!match) return null
  const child = match[2] && match[2] !== '/' ? match[2] : ''
  return { contractPath: match[1], child }
}

/** Called synchronously by Lux routing, including routes with a dedicated injector. */
function onLuxRentNavigation() {
  if (luxRentLastPath === window.location.pathname) return
  luxRentLastPath = window.location.pathname
  const route = luxRentRoute()
  if (luxProratedCloseModal) luxProratedCloseModal()
  if (
    !route ||
    (luxRentReturnPath && route.contractPath !== luxRentReturnPath)
  ) {
    luxRentReturnPath = null
    luxRentRestoreUntil = 0
  }
  if (
    route &&
    /^\/rent-plan\/(?:create|[^/]+\/edit)(?:\/|$)/.test(route.child)
  ) {
    luxRentReturnPath = route.contractPath
    luxRentRestoreUntil = 0
  } else if (
    route &&
    !route.child &&
    route.contractPath === luxRentReturnPath
  ) {
    // Allow lazy-loaded editors to mount, but do not retain a preference indefinitely.
    luxRentRestoreUntil = Date.now() + 15000
    luxRentRestoreAttempt = 0
  } else {
    luxRentReturnPath = null
    luxRentRestoreUntil = 0
  }
  if (route) mountLuxProratedFirstMonthRent()
  else unmountLuxProratedFirstMonthRent()
}

function luxRentSectionHeader(editor) {
  return (
    editor &&
    editor.querySelector(
      '[data-testid="primary-section-wrapper-rent-plan-section-header"]'
    )
  )
}

function luxRestoreRentSection(editor) {
  if (!luxRentRestoreUntil) return
  const route = luxRentRoute()
  if (
    Date.now() > luxRentRestoreUntil ||
    !route ||
    route.child ||
    route.contractPath !== luxRentReturnPath
  ) {
    luxRentRestoreUntil = 0
    luxRentReturnPath = null
    return
  }
  if (isMietzahlungStep(editor)) {
    luxRentRestoreUntil = 0
    luxRentReturnPath = null
    return
  }
  const header = luxRentSectionHeader(editor)
  if (header && Date.now() - luxRentRestoreAttempt > 1000) {
    luxRentRestoreAttempt = Date.now()
    luxRentRestoring = true
    try {
      header.click()
    } finally {
      luxRentRestoring = false
    }
  }
}

/** Remount only the native rent section. Its mount effect fetches the full list,
 * including allocations and SEV groups, through Impower's own API/state code. */
async function refreshLuxRentSection(editor, contractPath) {
  const isCurrent = () =>
    window.location.pathname === contractPath &&
    editor.isConnected &&
    getActiveContractEditor() === editor
  const header = luxRentSectionHeader(editor)
  if (!isCurrent() || !header || !isMietzahlungStep(editor)) return false
  header.click()
  let cancelled = false
  const onUserStep = (event) => {
    if (event.target.closest('.section-header')) cancelled = true
  }
  editor.addEventListener('click', onUserStep, true)
  try {
    // React must commit the unmount before reopening; two clicks in one tick are batched.
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 50))
      if (cancelled || !isCurrent()) return false
      if (!isMietzahlungStep(editor)) {
        editor.removeEventListener('click', onUserStep, true)
        header.click()
        return true
      }
    }
    return false
  } finally {
    editor.removeEventListener('click', onUserStep, true)
  }
}

function installLuxRentStyles() {
  if (document.getElementById('lux-rent-flow-styles')) return
  const style = document.createElement('style')
  style.id = 'lux-rent-flow-styles'
  style.textContent = `
    .lux-prorated-rent-dialog { border:0; padding:0; width:calc(100vw - 32px); max-width:640px;
      max-height:calc(100dvh - 32px); overflow:auto; border-radius:8px; color:#222;
      box-shadow:0 8px 32px #0003; font-family:inherit; font-size:14px; line-height:1.45; }
    .lux-prorated-rent-dialog::backdrop { background:rgba(0,0,0,.45); }
    .lux-prorated-rent-dialog *, .lux-prorated-rent-dialog { box-sizing:border-box; }
    .lux-prorated-rent-dialog .lux-pror-actions { display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap; margin-top:16px; }
    .lux-prorated-rent-dialog .lux-pror-dates { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; }
    .RentPlanEditor .PageHeader { height:auto; min-height:80px; flex-wrap:wrap; gap:12px; }
    .RentPlanEditor .PageHeader .left-side { min-width:0; flex:1 1 180px; }
    .RentPlanEditor .PageHeader .right-side { min-width:0; max-width:100%; flex-wrap:wrap; gap:8px; }
    .RentPlanEditor .PageHeader .header-buttons { flex-wrap:wrap; gap:8px; min-width:0; }
    .RentPlanEditor .PageHeader .subtitle-wrapper:has(*) { height:auto; }
    @media (max-width:1100px) {
      .overlay-wrapper:has(> .overlay-page > .RentPlanEditor) > .overlay-nav-link { display:none; }
      .overlay-page:has(> .RentPlanEditor) { min-width:0; width:100% !important; }
      .RentPlanEditor .PageHeader { padding:12px 16px; }
      .RentPlanEditor .PageHeader .left-side, .RentPlanEditor .PageHeader .right-side { margin:0; }
      .RentPlanEditor .PageHeader .right-side > :not(:first-child) { margin-left:0; }
      .RentPlanEditor .PageContent { padding:16px; }
      .RentPlanEditor .section-content-with-padding { padding:16px; }
      .RentPlanEditor .ant-col { min-width:0; }
      .RentPlanEditor .ant-row > .ant-col { flex:0 0 100%; max-width:100%; }
    }
    @media (max-width:480px) {
      .lux-prorated-rent-dialog .lux-pror-dates { grid-template-columns:1fr; }
      .lux-prorated-rent-dialog .lux-pror-actions > button { width:100%; }
    }
  `
  document.head.appendChild(style)
}

function luxUpper(s) {
  return (s == null ? '' : String(s)).toLocaleUpperCase('de-DE')
}

function looksLikeRentPlanTableText(raw) {
  const t = luxUpper(raw)
  if (!t) return false
  if (t.includes('KALTMIETE') || t.includes('WARMMIETE') || t.includes('PAUSCHALMIETE') || t.includes('NETTOMIETE')) {
    return true
  }
  return t.includes('ÄNDERUNGSDATUM') && t.includes('GESAMT')
}

function isShownBox(el, requireOpacity) {
  if (!el) return false
  const st = window.getComputedStyle(el)
  if (st.display === 'none' || st.visibility === 'hidden') return false
  if (requireOpacity && st.opacity === '0') return false
  const r = el.getBoundingClientRect()
  return r.width > 80 && r.height > 80
}

function lastShownContractEditorIn(root) {
  const editors = root.querySelectorAll(LUX_CONTRACT_EDITOR_SELECTOR)
  for (let i = editors.length - 1; i >= 0; i--) {
    if (isShownBox(editors[i], true)) return editors[i]
  }
  return null
}

/**
 * Impower often keeps multiple contract editors in the DOM; the overlay editor is usually
 * not `document.querySelector`’s first match — that made the banner never mount.
 * Overlay hosts are stacked last-on-top, so a leftover visible tenant overlay must not
 * win over the SEV owner contract the user is actually on.
 */
function getActiveContractEditor() {
  const overlayHosts = document.querySelectorAll(LUX_OVERLAY_HOST_SELECTOR)
  for (let i = overlayHosts.length - 1; i >= 0; i--) {
    const host = overlayHosts[i]
    if (!isShownBox(host, true)) continue
    const editor = lastShownContractEditorIn(host)
    if (editor) return editor
  }
  const all = document.querySelectorAll(LUX_CONTRACT_EDITOR_SELECTOR)
  let best = null
  let bestArea = 0
  for (let i = 0; i < all.length; i++) {
    const el = all[i]
    if (!isShownBox(el, false)) continue
    const r = el.getBoundingClientRect()
    const area = r.width * r.height
    if (area >= bestArea) {
      bestArea = area
      best = el
    }
  }
  return best
}

function parseGermanNumber(raw) {
  if (raw == null) return null
  const s = String(raw)
    .replace(/\s/g, '')
    .replace(/\u00a0/g, '')
    .replace(/€/g, '')
    .trim()
  if (!s) return null
  if (/^[+-]?\d{1,2}\.\d{1,2}\.\d{4}$/.test(s)) return null
  let normalized = s
  if (s.includes(',')) {
    if (!/^[+-]?(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d+)?$/.test(s)) return null
    normalized = s.replace(/\./g, '').replace(',', '.')
  } else if (/^[+-]?\d{1,3}(?:\.\d{3})+$/.test(s)) {
    normalized = s.replace(/\./g, '')
  } else if (!/^[+-]?\d+(?:\.\d+)?$/.test(s)) {
    return null
  }
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

function daysInCalendarMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate()
}

/** Inclusive days from move-in day (1-based) to end of calendar month; factor = num / denom. */
function factorActualCalendar(moveInY, moveInM0, moveInDay) {
  const dim = daysInCalendarMonth(moveInY, moveInM0)
  if (moveInDay < 1 || moveInDay > dim) return null
  const num = dim - moveInDay + 1
  return { factor: num / dim, label: `${num} von ${dim} Kalendertagen (${(num / dim * 100).toFixed(2)} %)` }
}

/** 30E/360-style: each month counts as 30 days; from move-in day d, inclusive days = 30 - d + 1 (d capped at 30). */
function factor30360(moveInDay) {
  const d = Math.min(Math.max(moveInDay, 1), 30)
  const num = 30 - d + 1
  return { factor: num / 30, label: `30/360: ${num} von 30 Tagen (${(num / 30 * 100).toFixed(2)} %)` }
}

function findRentPlanAddButton(pageContent) {
  if (!pageContent || typeof pageContent.querySelector !== 'function') return null
  const byTestId = pageContent.querySelector('[data-testid="rent-plan-overview-add-button"]')
  if (byTestId) return byTestId
  const buttons = pageContent.querySelectorAll('button')
  for (let i = 0; i < buttons.length; i++) {
    const t = luxUpper(buttons[i].textContent || '')
    if (t.includes('MIETE HINZUFÜGEN')) return buttons[i]
  }
  return null
}

function findRentTableWrapper(pageContent) {
  if (!pageContent || typeof pageContent.querySelectorAll !== 'function') return null
  const byTableId = pageContent.querySelector('#RentPlanOverviewTable')
  if (byTableId) {
    const wrap = byTableId.closest('.ant-table-wrapper')
    if (wrap) return wrap
  }
  const wrappers = pageContent.querySelectorAll('.ant-table-wrapper')
  for (let i = 0; i < wrappers.length; i++) {
    const w = wrappers[i]
    if (looksLikeRentPlanTableText(w.textContent || '')) return w
  }
  const anyTable = pageContent.querySelector('.ant-table')
  if (anyTable && looksLikeRentPlanTableText(anyTable.textContent || '')) {
    return anyTable.closest('.ant-table-wrapper') || anyTable.parentElement
  }
  return null
}

function isMietzahlungStep(editor) {
  const pc = editor.querySelector('.PageContent')
  if (!pc) return false
  if (findRentPlanAddButton(pc)) return true
  if (pc.querySelector('#RentPlanOverviewTable')) return true
  const u = luxUpper(pc.textContent || '')
  return u.includes('MIETE HINZUFÜGEN') && looksLikeRentPlanTableText(u)
}

function readTableHeadersAndLastRowCells(tableWrapper) {
  if (!tableWrapper || typeof tableWrapper.querySelector !== 'function') return null
  const thead = tableWrapper.querySelector('thead.ant-table-thead')
  const tbody = tableWrapper.querySelector('tbody.ant-table-tbody')
  if (!thead || !tbody) return null
  const headerTr = thead.querySelector('tr')
  const headers = headerTr
    ? Array.from(headerTr.querySelectorAll('th')).map((th) => (th.textContent || '').replace(/\s+/g, ' ').trim())
    : []
  const rows = Array.from(tbody.querySelectorAll('tr.ant-table-row')).filter(
    (r) => !r.classList.contains('ant-table-measure-row')
  )
  if (!rows.length) return { headers, cells: [] }
  const last = rows[rows.length - 1]
  const cells = Array.from(last.querySelectorAll('td.ant-table-cell'))
  return { headers, cells }
}

function readLastRowNumericCells(tableWrapper) {
  const tableData = readTableHeadersAndLastRowCells(tableWrapper)
  if (!tableData) return { headers: [], values: [] }
  const { headers, cells } = tableData
  const values = cells.map((td) => parseGermanNumber(td.textContent || ''))
  return { headers, values }
}

function readLastRowCellTexts(tableWrapper) {
  const tableData = readTableHeadersAndLastRowCells(tableWrapper)
  if (!tableData || !tableData.cells.length) return null
  const { headers, cells } = tableData
  const texts = cells.map((td) => (td.textContent || '').replace(/\s+/g, ' ').trim())
  return { headers, texts }
}

function parseGermanDateCellToIso(text) {
  const m = String(text)
    .trim()
    .match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/)
  if (!m) return null
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
}

function headerNorm(h) {
  return luxUpper(String(h || '').replace(/\s+/g, ''))
}

function changeDateColumnIndex(headers) {
  const n = headers.length
  for (let i = 0; i < n; i++) {
    if (headerNorm(headers[i]).includes('ÄNDERUNGSDATUM')) return i
  }
  return -1
}

/**
 * Latest Änderungsdatum among all table rows = effective start of the newest rent / exchange-plan row
 * (Impower lists versions; we take the chronologically latest date in that column).
 */
function readLatestExchangePlanStartIso(tableWrapper) {
  if (!tableWrapper || typeof tableWrapper.querySelector !== 'function') return null
  const thead = tableWrapper.querySelector('thead.ant-table-thead')
  const tbody = tableWrapper.querySelector('tbody.ant-table-tbody')
  if (!thead || !tbody) return null
  const headerTr = thead.querySelector('tr')
  const headers = headerTr
    ? Array.from(headerTr.querySelectorAll('th')).map((th) => (th.textContent || '').replace(/\s+/g, ' ').trim())
    : []
  const changeIdx = changeDateColumnIndex(headers)
  if (changeIdx < 0) return null
  const rows = Array.from(tbody.querySelectorAll('tr.ant-table-row')).filter(
    (r) => !r.classList.contains('ant-table-measure-row')
  )
  let bestIso = null
  let bestMs = -Infinity
  for (let r = 0; r < rows.length; r++) {
    const cells = Array.from(rows[r].querySelectorAll('td.ant-table-cell'))
    const n = Math.min(headers.length, cells.length)
    if (changeIdx >= n) continue
    const iso = parseGermanDateCellToIso(cells[changeIdx].textContent || '')
    if (!iso) continue
    const ms = new Date(`${iso}T12:00:00`).getTime()
    if (ms > bestMs) {
      bestMs = ms
      bestIso = iso
    }
  }
  return bestIso
}

/** Impower row keys are often UI nanoids — we match API plans via Änderungsdatum + Gesamt. */
function readLastRowMatchHints(tableWrapper) {
  const raw = readLastRowCellTexts(tableWrapper)
  if (!raw) return { changeDateIso: null, planTotal: null }
  const { headers, texts } = raw
  if (headers.length !== texts.length) {
    /* Ant fixed columns can desync th/td counts — still try best-effort by index min length */
  }
  const n = Math.min(headers.length, texts.length)

  const changeIdx = changeDateColumnIndex(headers.slice(0, n))

  let gesamtIdx = -1
  for (let i = n - 1; i >= 0; i--) {
    const hn = headerNorm(headers[i])
    if (hn === 'GESAMT' || (hn.length >= 6 && hn.endsWith('GESAMT'))) {
      gesamtIdx = i
      break
    }
  }

  let changeDateIso = null
  if (changeIdx >= 0 && texts[changeIdx]) changeDateIso = parseGermanDateCellToIso(texts[changeIdx])

  if (!changeDateIso) {
    for (let i = 0; i < n; i++) {
      const hn = headerNorm(headers[i])
      if (hn.includes('NÄCHSTEAUSFÜHRUNG') || hn.includes('LETZTEAUSFÜHRUNG')) {
        const iso = parseGermanDateCellToIso(texts[i])
        if (iso) {
          changeDateIso = iso
          break
        }
      }
    }
  }

  let planTotal = null
  if (gesamtIdx >= 0 && texts[gesamtIdx] != null) planTotal = parseGermanNumber(texts[gesamtIdx])
  return { changeDateIso, planTotal }
}

function readLastRowExchangePlanId(tableWrapper) {
  if (!tableWrapper || typeof tableWrapper.querySelector !== 'function') return null
  const tbody = tableWrapper.querySelector('tbody.ant-table-tbody')
  if (!tbody) return null
  const rows = Array.from(tbody.querySelectorAll('tr.ant-table-row')).filter(
    (r) => !r.classList.contains('ant-table-measure-row')
  )
  if (!rows.length) return null
  const last = rows[rows.length - 1]
  const key = last.getAttribute('data-row-key')
  return key != null && String(key).trim() !== '' ? String(key).trim() : null
}

function isoDayBefore(isoYmd) {
  if (!isoYmd || !/^\d{4}-\d{2}-\d{2}$/.test(isoYmd)) return ''
  const d = new Date(`${isoYmd}T12:00:00`)
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

function getUnitContractIdFromPath() {
  const m = window.location.pathname.match(/contract\/all\/edit\/([^/?]+)/)
  return m ? m[1] : null
}

function openProratedModal({ pageContent }) {
  if (luxProratedCloseModal) luxProratedCloseModal()
  installLuxRentStyles()
  const contractPath = window.location.pathname
  const editor = pageContent.closest(LUX_CONTRACT_EDITOR_SELECTOR)
  const previousFocus = document.activeElement
  const overlay = document.createElement('dialog')
  overlay.className = 'lux-prorated-rent-dialog'
  overlay.setAttribute('aria-labelledby', 'lux-pror-title')
  overlay.setAttribute('aria-modal', 'true')
  const closeModal = () => {
    overlay.close()
    overlay.remove()
    if (luxProratedCloseModal === closeModal) luxProratedCloseModal = null
    if (previousFocus && previousFocus.isConnected) previousFocus.focus()
  }
  luxProratedCloseModal = closeModal
  overlay.addEventListener('cancel', (event) => {
    event.preventDefault()
    closeModal()
  })
  const box = document.createElement('div')
  // Typography: title 18px · body 14px · meta 12px (only these three)
  box.style.cssText =
    'background:#fff;padding:24px;width:100%;font-family:inherit;font-size:14px;line-height:1.45;color:#222;'

  const today = new Date()
  const defaultDate = today.toISOString().slice(0, 10)
  const inputStyle =
    'width:100%;padding:8px 10px;border:1px solid #d9d9d9;border-radius:4px;font:inherit;box-sizing:border-box;'

  box.innerHTML = `
    <h2 id="lux-pror-title" style="margin:0 0 8px;font-size:18px;font-weight:700;line-height:1.3;">Anteilige Miete (erster Monat)</h2>
    <p style="margin:0 0 16px;color:#555;">
      Voraussetzung: In Impower ist zuerst der <strong>volle Monatsplan</strong> angelegt. Hier berechnen Sie den Anteil für den unvollständigen ersten Monat und können die Werte in die zusätzliche Mietzeile übernehmen.
    </p>
    <div style="margin-bottom:14px;">
      <label for="lux-pror-movein" style="display:block;margin-bottom:6px;font-weight:600;">Mietbeginn (Tag im Monat)</label>
      <input type="date" id="lux-pror-movein" value="${defaultDate}" style="${inputStyle}" />
    </div>
    <div style="margin-bottom:16px;">
      <span style="display:block;margin-bottom:8px;font-weight:600;">Berechnungsmethode</span>
      <label style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px;cursor:pointer;">
        <input type="radio" name="lux-pror-method" value="actual" checked style="margin-top:3px;" />
        <span><strong>Kalendertage im Monat</strong> — z. B. Einzug 17.08.: Faktor = (31 − 17 + 1) / 31.</span>
      </label>
      <label style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px;cursor:pointer;">
        <input type="radio" name="lux-pror-method" value="30360" style="margin-top:3px;" />
        <span><strong>30 / 360</strong> — Monat zählt 30 Tage; Faktor = (30 − Tag + 1) / 30 (Tag max. 30).</span>
      </label>
      <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;">
        <input type="radio" name="lux-pror-method" value="percent" style="margin-top:3px;" />
        <span><strong>Eigener %-Satz</strong></span>
      </label>
      <input type="text" id="lux-pror-pct" inputmode="decimal" placeholder="z. B. 48,39" disabled autocomplete="off"
        style="margin:8px 0 0 28px;max-width:200px;${inputStyle}" />
    </div>
    <div id="lux-pror-result" style="margin-bottom:16px;padding:12px;background:#f7f7f7;border-radius:6px;border:1px solid #eee;"></div>
    <div style="margin-top:4px;padding-top:16px;border-top:1px solid #eee;">
      <h3 style="margin:0 0 8px;font-size:14px;font-weight:600;">Rumpfmonat in Impower anlegen</h3>
      <p style="margin:0 0 12px;color:#555;">
        Es wird ein <strong>neuer Entwurf</strong> erzeugt: Zeitraum unten, Beträge = letzte Tabellenzeile × Faktor (alle Spalten wie oben).
        Impower findet den Plan über <strong>Gesamt</strong> und <strong>Änderungsdatum</strong> der letzten Tabellenzeile (nicht über den Ant-Design-Zeilen-Key). Letzte Zeile = zu teilender Vollplan.
      </p>
      <div style="margin-bottom:10px;">
        <label for="lux-pror-haupt" style="display:block;margin-bottom:4px;font-weight:600;">Hauptmiete gültig ab (erster voller Monat)</label>
        <input type="date" id="lux-pror-haupt" style="${inputStyle}" />
        <span style="font-size:12px;color:#888;">Vorbelegung: jüngstes „Änderungsdatum“ in der Miettabelle. „Rumpf gültig bis“ = Vortag davon (anpassbar).</span>
      </div>
      <div class="lux-pror-dates" style="margin-bottom:4px;">
        <div>
          <label for="lux-pror-rumpf-von" style="display:block;margin-bottom:4px;font-weight:600;">Rumpf gültig von</label>
          <input type="date" id="lux-pror-rumpf-von" value="${defaultDate}" style="${inputStyle}" />
        </div>
        <div>
          <label for="lux-pror-rumpf-bis" style="display:block;margin-bottom:4px;font-weight:600;">Rumpf gültig bis (inkl.)</label>
          <input type="date" id="lux-pror-rumpf-bis" style="${inputStyle}" />
        </div>
      </div>
    </div>
    <div class="lux-pror-actions">
      <button type="button" id="lux-pror-close" class="ant-btn ant-btn-default Button" style="flex:0 0 auto;font:inherit;">Schließen</button>
      <button type="button" id="lux-pror-copy" class="ant-btn ant-btn-default Button" style="flex:0 0 auto;font:inherit;">In Zwischenablage</button>
      <button type="button" id="lux-pror-create" class="ant-btn ant-btn-primary Button" style="flex:0 0 auto;background:#111;border-color:#111;color:#fff;font:inherit;">Rumpfmiete anlegen</button>
    </div>
  `

  overlay.appendChild(box)
  document.body.appendChild(overlay)
  overlay.showModal()

  const moveInInput = box.querySelector('#lux-pror-movein')
  const pctInput = box.querySelector('#lux-pror-pct')
  const resultEl = box.querySelector('#lux-pror-result')
  let lastSnapshot = { headers: [], scaled: [], factor: 0, meta: '' }

  const methodRadios = () =>
    box.querySelectorAll('input[name="lux-pror-method"]')

  methodRadios().forEach((r) => {
    r.addEventListener('change', () => {
      const v = box.querySelector(
        'input[name="lux-pror-method"]:checked'
      )?.value
      pctInput.disabled = v !== 'percent'
      if (v === 'percent' && !pctInput.value) pctInput.focus()
      recalc()
    })
  })

  const recalc = () => {
    // Invalidate first so any early return cannot leave a stale factor for „Anlegen“ / copy.
    lastSnapshot = { headers: [], scaled: [], factor: 0, meta: '' }
    const tableWrapper = findRentTableWrapper(pageContent)
    const { headers, values } = readLastRowNumericCells(tableWrapper)
    const dStr = moveInInput.value
    const parts = dStr.split('-').map((x) => parseInt(x, 10))
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      resultEl.textContent = 'Bitte gültiges Datum wählen.'
      return
    }
    const y = parts[0]
    const m0 = parts[1] - 1
    const day = parts[2]
    const method = box.querySelector('input[name="lux-pror-method"]:checked')?.value || 'actual'

    let factorInfo = null
    if (method === 'actual') {
      factorInfo = factorActualCalendar(y, m0, day)
    } else if (method === '30360') {
      factorInfo = factor30360(day)
    } else {
      const p = parseGermanNumber(pctInput.value)
      if (p == null || p < 0 || p > 100) {
        resultEl.textContent = 'Bitte einen Prozentsatz zwischen 0 und 100 eingeben.'
        return
      }
      factorInfo = { factor: p / 100, label: `Manuell: ${p.toFixed(2).replace('.', ',')} %` }
    }

    if (!factorInfo || factorInfo.factor == null || factorInfo.factor <= 0 || factorInfo.factor > 1) {
      resultEl.textContent = 'Für das gewählte Datum konnte kein gültiger Anteil ermittelt werden.'
      return
    }

    const f = factorInfo.factor
    const lines = []
    const refId = readLastRowExchangePlanId(tableWrapper)
    const hints = readLastRowMatchHints(tableWrapper)
    const hintParts = []
    const codeStyle = 'font:inherit;font-size:12px;'
    if (hints.changeDateIso) {
      hintParts.push(`Änderungsdatum → <code style="${codeStyle}">${hints.changeDateIso}</code>`)
    }
    if (hints.planTotal != null) {
      hintParts.push(
        `Gesamt → <code style="${codeStyle}">${hints.planTotal.toFixed(2).replace('.', ',')} €</code>`
      )
    }
    if (hintParts.length) {
      lines.push(
        `<div style="font-size:12px;color:#666;margin-bottom:8px;">Referenz für „Anlegen“ (letzte Zeile): ${hintParts.join(', ')}</div>`
      )
    } else if (refId) {
      lines.push(
        `<div style="font-size:12px;color:#666;margin-bottom:8px;">Tabellen-Key: <code style="${codeStyle}">${refId}</code> (wird nur genutzt, wenn Impower dieselbe ID kennt)</div>`
      )
    }
    lines.push(`<div style="margin-bottom:8px;"><strong>${factorInfo.label}</strong></div>`)
    const scaled = []
    let any = false
    for (let i = 0; i < values.length; i++) {
      const base = values[i]
      if (base == null) continue
      const h = headers[i] || `Spalte ${i + 1}`
      const scaledVal = Math.round(base * f * 100) / 100
      scaled.push({ header: h, base, scaled: scaledVal })
      lines.push(
        `<div style="display:flex;justify-content:space-between;gap:12px;border-bottom:1px solid #e8e8e8;padding:4px 0;">
          <span style="color:#444;">${h}</span>
          <span><span style="color:#888;">${base.toFixed(2).replace('.', ',')} €</span> → <strong>${scaledVal.toFixed(2).replace('.', ',')} €</strong></span>
        </div>`
      )
      any = true
    }
    if (!any) {
      lines.push(
        '<div style="color:#a61d24;">In der letzten Tabellenzeile wurden keine Beträge erkannt. Legen Sie zuerst die volle Monatsmiete an oder tragen Sie die Beträge manuell in Impower ein und öffnen Sie den Rechner erneut.</div>'
      )
    }
    resultEl.innerHTML = lines.join('')
    lastSnapshot = { headers, scaled, factor: f, meta: factorInfo.label }
  }

  moveInInput.addEventListener('change', recalc)
  moveInInput.addEventListener('input', recalc)
  pctInput.addEventListener('input', recalc)

  const hauptInput = box.querySelector('#lux-pror-haupt')
  const rumpfVonInput = box.querySelector('#lux-pror-rumpf-von')
  const rumpfBisInput = box.querySelector('#lux-pror-rumpf-bis')

  const twInit = findRentTableWrapper(pageContent)
  const hauptFromPlan = readLatestExchangePlanStartIso(twInit)
  if (hauptFromPlan) {
    hauptInput.value = hauptFromPlan
    rumpfBisInput.value = isoDayBefore(hauptFromPlan)
  }

  moveInInput.addEventListener('input', () => {
    if (!rumpfVonInput.dataset.touched) rumpfVonInput.value = moveInInput.value
  })
  rumpfVonInput.addEventListener('input', () => {
    rumpfVonInput.dataset.touched = '1'
  })
  hauptInput.addEventListener('input', () => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(hauptInput.value)) {
      rumpfBisInput.value = isoDayBefore(hauptInput.value)
    }
  })

  box.querySelector('#lux-pror-close').addEventListener('click', closeModal)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeModal()
    }
  })

  box.querySelector('#lux-pror-copy').addEventListener('click', async () => {
    const lines = [
      lastSnapshot.meta,
      `Faktor: ${lastSnapshot.factor.toFixed(6)}`,
    ]
    lastSnapshot.scaled.forEach((row) => {
      lines.push(`${row.header}\t${row.scaled.toFixed(2).replace('.', ',')}`)
    })
    const text = lines.join('\n')
    try {
      await navigator.clipboard.writeText(text)
      alert('In Zwischenablage kopiert.')
    } catch {
      alert(text)
    }
  })

  const createBtn = box.querySelector('#lux-pror-create')
  createBtn.addEventListener('click', async () => {
    if (
      createBtn.disabled ||
      window.location.pathname !== contractPath ||
      !editor.isConnected
    )
      return
    const tw = findRentTableWrapper(pageContent)
    const planId = readLastRowExchangePlanId(tw)
    const hints = readLastRowMatchHints(tw)
    if (hints.planTotal == null || Number.isNaN(hints.planTotal)) {
      alert(
        'In der letzten Zeile konnte kein Gesamtbetrag gelesen werden — Referenz für Impower fehlt.'
      )
      return
    }
    if (!hints.changeDateIso) {
      alert(
        'Änderungsdatum der letzten Zeile nicht lesbar (TT.MM.JJJJ erwartet). Bitte Spalte „Änderungsdatum“ einblenden oder Zeile prüfen.'
      )
      return
    }
    const vf = rumpfVonInput.value
    const vt = rumpfBisInput.value
    if (!/^\d{4}-\d{2}-\d{2}$/.test(vf) || !/^\d{4}-\d{2}-\d{2}$/.test(vt)) {
      alert(
        'Bitte „Rumpf gültig von“ und „Rumpf gültig bis“ vollständig ausfüllen.'
      )
      return
    }
    if (new Date(`${vf}T12:00:00`) > new Date(`${vt}T12:00:00`)) {
      alert('„Gültig von“ darf nicht nach „gültig bis“ liegen.')
      return
    }
    const uc = getUnitContractIdFromPath()
    if (!uc) {
      alert('Vertrags-ID aus der URL nicht lesbar.')
      return
    }
    let token = null
    try {
      token = JSON.parse(localStorage.getItem('auth') || '{}').token
    } catch {
      token = null
    }
    if (!token) {
      alert('Nicht angemeldet.')
      return
    }
    if (!lastSnapshot.factor || lastSnapshot.factor <= 0) {
      alert('Bitte zuerst eine gültige Berechnung oben anzeigen (Faktor > 0).')
      return
    }
    createBtn.disabled = true
    try {
      const res = await fetch(
        `${baseApiUrl}/api/impowerX/unit-contracts/rumpfmonat-rent-plan`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            unitContractId: parseInt(uc, 10),
            referenceExchangePlanId: planId || undefined,
            matchChangeDate: hints.changeDateIso || undefined,
            matchPlanTotal: hints.planTotal,
            validFrom: vf,
            validTo: vt,
            prorationFactor: lastSnapshot.factor,
          }),
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const detail = data.details ? `\n\n${data.details}` : ''
        alert(
          `Anlegen fehlgeschlagen: ${data.error || res.statusText || res.status}${detail}`
        )
        return
      }
      closeModal()
      const refreshed = await refreshLuxRentSection(editor, contractPath)
      if (!refreshed && window.location.pathname === contractPath) {
        alert(
          'Rumpfmiete angelegt. Bitte Mietzahlung erneut öffnen, um die Tabelle zu aktualisieren.'
        )
      }
    } catch (e) {
      alert('Netzwerkfehler: ' + (e && e.message ? e.message : String(e)))
    } finally {
      createBtn.disabled = false
    }
  })

  recalc()
}

function removeProratedBannerFromPageContent(pageContent) {
  if (!pageContent) return
  pageContent
    .querySelectorAll(`.${LUX_PRORATED_HOST_CLASS}`)
    .forEach((h) => h.remove())
  pageContent
    .querySelectorAll(
      'h3.subsection-title-wrapper[data-lux-prorated-header-flex="1"]'
    )
    .forEach((h3) => {
      delete h3.dataset.luxProratedHeaderFlex
      h3.style.removeProperty('display')
      h3.style.removeProperty('align-items')
      h3.style.removeProperty('flex-wrap')
      h3.style.removeProperty('gap')
    })
}

function ensureBanner(pageContent) {
  if (!pageContent) return

  const addBtn = findRentPlanAddButton(pageContent)
  if (!addBtn || !addBtn.parentNode) return

  const existingHosts = pageContent.querySelectorAll(
    `.${LUX_PRORATED_HOST_CLASS}`
  )
  if (
    existingHosts.length === 1 &&
    addBtn.previousElementSibling === existingHosts[0]
  )
    return
  existingHosts.forEach((h) => h.remove())

  console.info(
    '[Lux] EHV-770: Mietzahlung erkannt — Steuerung links neben „Miete hinzufügen“ eingefügt.'
  )

  const host = document.createElement('div')
  host.className = LUX_PRORATED_HOST_CLASS
  host.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'flex-shrink:0',
    'margin-right:10px',
    'pointer-events:auto',
  ].join(';')

  host.innerHTML = `
    <button type="button" class="ant-btn ant-btn-default Button lux-prorated-open-btn"
      title="Vollmiete zuerst anlegen. Anteil ersten Monat: Kalendertage, 30/360 oder eigener %-Satz."
      style="pointer-events:auto;cursor:pointer;flex-shrink:0;white-space:nowrap;overflow:visible;max-width:none;">
      <span style="white-space:nowrap;">Anteilige Miete</span>
    </button>
  `

  const openBtn = host.querySelector('.lux-prorated-open-btn')
  openBtn.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    openProratedModal({ pageContent })
  })

  addBtn.parentNode.insertBefore(host, addBtn)

  const h3 = addBtn.closest('h3.subsection-title-wrapper')
  if (h3 && !h3.dataset.luxProratedHeaderFlex) {
    h3.dataset.luxProratedHeaderFlex = '1'
    h3.style.display = 'flex'
    h3.style.alignItems = 'center'
    h3.style.flexWrap = 'wrap'
    h3.style.gap = '8px'
  }
}

let luxProratedRentPollId = null

/** Same idea as `entryPoints.js` `contractsPage` — stop polling when SPA navigates away. */
function isLuxProratedRentContractsSpaPath() {
  return /contract\/all\/edit/.test(window.location.pathname)
}

function unmountLuxProratedFirstMonthRent() {
  if (luxProratedRentPollId == null) return
  clearInterval(luxProratedRentPollId)
  document.removeEventListener('click', onLuxRentSectionClick, true)
  luxProratedRentPollId = null
}

function mountLuxProratedFirstMonthRent() {
  if (luxProratedRentPollId != null) return
  installLuxRentStyles()
  document.addEventListener('click', onLuxRentSectionClick, true)
  console.info('[Lux] EHV-770: Mietzahlung-Proration aktiv (Polling).')
  luxProratedRentPollId = setInterval(() => {
    // The host may replace history methods after Lux has installed its router.
    if (luxRentLastPath !== window.location.pathname) onLuxRentNavigation()
    if (!isLuxProratedRentContractsSpaPath()) {
      unmountLuxProratedFirstMonthRent()
      return
    }
    const route = luxRentRoute()
    // Never inject into a background contract while a native child overlay is open.
    if (!route || route.child) return
    const editor = getActiveContractEditor()
    const pageContent = editor && editor.querySelector('.PageContent')
    if (!editor || !pageContent) return

    luxRestoreRentSection(editor)
    if (!isMietzahlungStep(editor)) {
      removeProratedBannerFromPageContent(pageContent)
      return
    }

    ensureBanner(pageContent)
  }, 400)
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    onLuxRentNavigation,
    refreshLuxRentSection,
    mountLuxProratedFirstMonthRent,
    unmountLuxProratedFirstMonthRent,
    openProratedModal,
    ensureBanner,
    getActiveContractEditor,
  }
}
